"use client";

import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/fun-ui/Dialog";
import { Button } from "@/components/fun-ui/Button";

export type DesktopInfo = {
  runtime: string;
  version: string;
  platform: string;
  channel: string;
  desktopDiagnosticsVersion?: number;
};

type DesktopUpdateCheckResult = {
  currentVersion?: string;
  current_version?: string;
  available: boolean;
  rid?: number;
  version?: string | null;
  notes?: string | null;
};

type DesktopInvoke = <T = unknown>(command: string, args?: Record<string, unknown>) => Promise<T>;

type DesktopWindow = Window & {
  __TAURI_INTERNALS__?: {
    invoke?: DesktopInvoke;
  };
  __FINDIN_DESKTOP_INFO__?: Partial<DesktopInfo>;
};

type DesktopUpdateDialogRenderProps = {
  desktopInfo: DesktopInfo | null;
  desktopError: string | null;
  isDesktopRuntime: boolean;
  isDesktop: boolean;
  checking: boolean;
  checkingUpdate: boolean;
  installing: boolean;
  checkNow: () => Promise<void>;
  checkForUpdates: () => Promise<void>;
};

type DesktopUpdateDialogProps = {
  autoCheck?: boolean;
  listenForGlobalCheck?: boolean;
  children?: (props: DesktopUpdateDialogRenderProps) => ReactNode;
};

function getDesktopInvoke(): DesktopInvoke | null {
  if (typeof window === "undefined") {
    return null;
  }

  const invoke = (window as DesktopWindow).__TAURI_INTERNALS__?.invoke;
  return typeof invoke === "function" ? invoke : null;
}

function getInjectedDesktopInfo(): DesktopInfo | null {
  if (typeof window === "undefined") {
    return null;
  }

  const info = (window as DesktopWindow).__FINDIN_DESKTOP_INFO__;
  if (!info) {
    return null;
  }

  return {
    runtime: info.runtime || "Findin 桌面端",
    version: info.version || "-",
    platform: info.platform || "-",
    channel: info.channel || "-",
    desktopDiagnosticsVersion: info.desktopDiagnosticsVersion,
  };
}

function normalizeError(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  return String(error || "操作失败，请稍后重试。");
}

export function DesktopUpdateDialog({
  autoCheck = false,
  listenForGlobalCheck = false,
  children,
}: DesktopUpdateDialogProps) {
  const [desktopInfo, setDesktopInfo] = useState<DesktopInfo | null>(null);
  const [checkResult, setCheckResult] = useState<DesktopUpdateCheckResult | null>(null);
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [desktopError, setDesktopError] = useState<string | null>(null);
  const [isDesktopRuntime, setIsDesktopRuntime] = useState(false);

  const isDesktop = !!desktopInfo;

  const loadDesktopInfo = useCallback(async () => {
    const injectedInfo = getInjectedDesktopInfo();
    const invoke = getDesktopInvoke();
    const runningInDesktop = !!injectedInfo || !!invoke;

    setIsDesktopRuntime(runningInDesktop);
    if (injectedInfo) {
      setDesktopInfo(injectedInfo);
      setDesktopError(null);
      return injectedInfo;
    }

    if (!runningInDesktop) {
      setDesktopInfo(null);
      setDesktopError(null);
      return null;
    }

    const fallbackInfo = {
      runtime: "Findin 桌面端",
      version: "-",
      platform: "-",
      channel: "-",
    };
    setDesktopInfo(fallbackInfo);
    setDesktopError(null);
    return fallbackInfo;
  }, []);

  const checkNow = useCallback(async () => {
    const invoke = getDesktopInvoke();
    if (!invoke) {
      toast.error("当前环境不是 Findin 桌面端，无法检查客户端更新。");
      return;
    }

    setChecking(true);
    setErrorMessage(null);

    try {
      const [info, result] = await Promise.all([
        loadDesktopInfo(),
        invoke<DesktopUpdateCheckResult>("check_desktop_update"),
      ]);
      const currentVersion = result?.currentVersion || result?.current_version || info?.version || "-";
      const normalizedResult = result
        ? {
          ...result,
          currentVersion,
          available: result.available,
        }
        : {
          currentVersion,
          available: false,
          version: null,
        };
      if (info) {
        setDesktopInfo({
          ...info,
          version: normalizedResult.currentVersion || info.version,
        });
      }
      setCheckResult(normalizedResult);
      setOpen(true);
    } catch (error) {
      const message = normalizeError(error);
      setErrorMessage(message);
      setCheckResult(null);
      setOpen(true);
    } finally {
      setChecking(false);
    }
  }, [loadDesktopInfo]);

  const installUpdate = useCallback(async () => {
    const invoke = getDesktopInvoke();
    if (!invoke) {
      toast.error("当前环境不是 Findin 桌面端，无法安装更新。");
      return;
    }

    setInstalling(true);
    setErrorMessage(null);

    try {
      await invoke("install_desktop_update");
      toast.success("更新已安装，请重启应用完成更新。");
    } catch (error) {
      const message = normalizeError(error);
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setInstalling(false);
    }
  }, []);

  useEffect(() => {
    void loadDesktopInfo();
  }, [loadDesktopInfo]);

  useEffect(() => {
    if (autoCheck && isDesktopRuntime) {
      void checkNow();
    }
  }, [autoCheck, checkNow, isDesktopRuntime]);

  useEffect(() => {
    if (!listenForGlobalCheck || typeof window === "undefined") {
      return;
    }

    const handleCheckUpdate = (event: Event) => {
      event.preventDefault();
      void checkNow();
    };

    window.addEventListener("findin-desktop:check-update", handleCheckUpdate);
    return () => {
      window.removeEventListener("findin-desktop:check-update", handleCheckUpdate);
    };
  }, [checkNow, listenForGlobalCheck]);

  const title = useMemo(() => {
    if (errorMessage) {
      return "检查更新失败";
    }

    if (checkResult?.available) {
      return "发现新版本";
    }

    return "Findin 已是最新版本";
  }, [checkResult?.available, errorMessage]);

  return (
    <>
      {children?.({
        desktopInfo,
        desktopError,
        isDesktopRuntime,
        isDesktop,
        checking,
        checkingUpdate: checking,
        installing,
        checkNow,
        checkForUpdates: checkNow,
      })}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[430px] gap-5 p-8 sm:max-w-[430px]">
          <DialogHeader className="gap-3 text-left">
            <DialogTitle className="text-2xl font-semibold text-foreground">
              {title}
            </DialogTitle>
            <DialogDescription className="text-left text-sm leading-6 text-muted-foreground">
              {errorMessage || (checkResult?.available
                ? "找到可用更新后，可选择立即更新并在安装完成后重启应用，或稍后再说。"
                : "当前客户端已经是最新版本，无需更新。")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 rounded-md border border-border bg-muted/40 p-4 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">当前版本</span>
              <span className="font-medium text-foreground">{checkResult?.currentVersion || desktopInfo?.version || "-"}</span>
            </div>
            {checkResult?.available ? (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">新版本</span>
                <span className="font-medium text-foreground">{checkResult.version || "-"}</span>
              </div>
            ) : null}
            {isDesktop ? (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">运行环境</span>
                <span className="font-medium text-foreground">{desktopInfo.runtime}</span>
              </div>
            ) : null}
          </div>

          <DialogFooter className="gap-3 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="min-w-[96px]"
              disabled={checking || installing}
              onClick={() => setOpen(false)}
            >
              {checkResult?.available ? "稍后再说" : "确认"}
            </Button>
            {checkResult?.available ? (
              <Button
                type="button"
                className="min-w-[112px]"
                disabled={checking || installing}
                onClick={installUpdate}
              >
                {installing ? "更新中..." : "立即更新"}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
